import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Book, Heart, Users, Clock, Award, ArrowRight, Download, Star, Shield, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URLs
const LOGO_LARGE = "https://customer-assets.emergentagent.com/job_d247ce4d-516b-48b6-b845-65d877cb78ad/artifacts/fz8hl5nv_light%20green%20logo.png";
const LOGO_SMALL = "https://customer-assets.emergentagent.com/job_d247ce4d-516b-48b6-b845-65d877cb78ad/artifacts/5khypy4w_light%20green%20logo%20-%20sm%20one%20line.png";

// Product data
const PRODUCT = {
  title: "Unmasking Marriage: The Relationship Reset",
  subtitle: "The Secrets To Reconnect Every Husband Needs to Know",
  author: "Mark Dawdy",
  price: 27,
  thumbnail: "https://customer-assets.emergentagent.com/job_d247ce4d-516b-48b6-b845-65d877cb78ad/artifacts/laq98j1o_unmasking%20marriage_thumbnail.png",
  pdfUrl: "https://customer-assets.emergentagent.com/job_d247ce4d-516b-48b6-b845-65d877cb78ad/artifacts/rrf4flq4_Unmasking%20Marriage%20The%20Relationship%20Reset.pdf"
};

// ========== SQUEEZE PAGE ==========
const SqueezePage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/leads`, {
        email,
        first_name: firstName,
        source: "squeeze_page",
        tags: ["free_checklist", "marriage_funnel"]
      });
      // Store email for later use
      localStorage.setItem("funnel_email", email);
      localStorage.setItem("funnel_name", firstName);
      navigate("/checklist");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark to-navy-darker" data-testid="squeeze-page">
      {/* Header */}
      <header className="py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <img src={LOGO_SMALL} alt="Mindful Forays" className="h-12" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-orange-accent/20 text-orange-accent px-4 py-2 rounded-full text-sm font-semibold mb-6">
            FREE CHECKLIST
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Discover The <span className="text-orange-accent">7 Secrets to Reconnect</span> Every Man Needs to Know
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Stop feeling like roommates. Start reconnecting with your wife using these science-backed daily habits.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8 px-6">
        <div className="max-w-md mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Get Your Free Checklist</h2>
              <p className="text-gray-300">Enter your details below for instant access</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="squeeze-form">
              <div>
                <input
                  type="text"
                  placeholder="Your First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-orange-accent transition-colors"
                  data-testid="squeeze-firstname"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Your Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-orange-accent transition-colors"
                  data-testid="squeeze-email"
                />
              </div>
              {error && <p className="text-red-400 text-sm" data-testid="squeeze-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="squeeze-submit"
              >
                {loading ? "Sending..." : "SEND ME THE FREE CHECKLIST"}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            <p className="text-gray-400 text-xs text-center mt-4">
              🔒 We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-white text-center mb-8">What You'll Discover:</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Heart, title: "Emotional Connection", desc: "Reignite the spark that brought you together" },
              { icon: Clock, title: "Daily Habits", desc: "Simple 10-minute rituals that transform your marriage" },
              { icon: Shield, title: "Build Trust", desc: "Create emotional safety she has been craving" }
            ].map((item, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-6 text-center border border-white/10">
                <item.icon className="w-10 h-10 text-orange-accent mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />)}
          </div>
          <p className="text-gray-300 italic mb-4">{`"These habits changed everything. We went from barely talking to actually enjoying each other's company again."`}</p>
          <p className="text-gray-400">— James T., Married 12 Years</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/10 text-center">
        <p className="text-gray-500 text-sm">© 2025 Mindful Forays. All rights reserved.</p>
      </footer>
    </div>
  );
};

// ========== SALES PAGE ==========
const SalesPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How is this different from other relationship books?",
      a: "This ebook focuses specifically on actionable daily habits backed by research from The Gottman Institute. It's not theory—it's a practical system you can start using today."
    },
    {
      q: "What if my wife isn't interested in working on our marriage?",
      a: "That's exactly why this guide is designed for husbands to take the lead. You don't need your wife's participation to start seeing changes. Your consistent effort will naturally influence the relationship."
    },
    {
      q: "How quickly will I see results?",
      a: "Many readers report noticing shifts within the first week of implementing the habits. However, lasting transformation takes consistent effort over 30-90 days."
    },
    {
      q: "Is there a guarantee?",
      a: "Yes! We offer a 30-day money-back guarantee. If you implement the habits and don't see any improvement in your relationship, simply email us for a full refund."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark to-navy-darker" data-testid="sales-page">
      {/* Header */}
      <header className="py-4 px-6 sticky top-0 bg-navy-dark/95 backdrop-blur-sm z-50 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <img src={LOGO_SMALL} alt="Mindful Forays" className="h-10" />
          <button
            onClick={() => navigate("/checkout")}
            className="bg-orange-accent hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            data-testid="header-buy-btn"
          >
            Get The Ebook - $27
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block bg-green-accent/20 text-green-accent px-4 py-2 rounded-full text-sm font-semibold mb-6">
              LIMITED TIME OFFER
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {PRODUCT.title}
            </h1>
            <p className="text-xl text-orange-accent font-semibold mb-4">
              {PRODUCT.subtitle}
            </p>
            <p className="text-gray-300 mb-8">
              Discover the 7 science-backed daily habits that will help you reconnect with your wife, 
              rebuild emotional intimacy, and transform your marriage—without expensive therapy or 
              grand gestures.
            </p>
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-400 line-through text-2xl">$47</span>
              <span className="text-4xl font-bold text-white">${PRODUCT.price}</span>
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">SAVE 43%</span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="w-full md:w-auto px-8 py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              data-testid="hero-buy-btn"
            >
              YES! I WANT TO SAVE MY MARRIAGE
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-gray-400 text-sm mt-4 flex items-center gap-2">
              <Shield className="w-4 h-4" /> 30-Day Money-Back Guarantee
            </p>
          </div>
          <div className="flex justify-center">
            <img
              src={PRODUCT.thumbnail}
              alt={PRODUCT.title}
              className="max-w-sm w-full rounded-xl shadow-2xl shadow-orange-accent/20"
              data-testid="product-image"
            />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-6 bg-navy-darker">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Does This Sound Familiar?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {[
              "You feel more like roommates than romantic partners",
              "Conversations have become transactional",
              "Physical intimacy has declined significantly",
              "You've tried 'date nights' but nothing changes",
              "You feel unappreciated despite everything you do",
              "You're not sure how to reconnect anymore"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/5 p-4 rounded-lg border border-white/10">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-red-400">✗</span>
                </div>
                <p className="text-gray-300">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-xl text-orange-accent font-semibold mt-10">
            You're not alone. And it's not your fault.
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Introducing: The 7 Marriage-Saving Habits
          </h2>
          <p className="text-gray-300 text-center mb-12 max-w-2xl mx-auto">
            Research from The Gottman Institute shows that small, consistent daily actions 
            are more powerful than grand gestures. This ebook gives you the exact system.
          </p>
          <div className="space-y-6">
            {[
              { num: 1, title: "The Morning Check-In", desc: "Start each day with intentional connection in just 5 minutes" },
              { num: 2, title: "The Two-Sentence Text", desc: "A simple message that keeps you connected throughout the day" },
              { num: 3, title: "The Presence Power-Up", desc: "Be truly present when you're together (not just physically there)" },
              { num: 4, title: "The Appreciation Anchor", desc: "Express gratitude in a way that actually lands" },
              { num: 5, title: "The Listening Loop", desc: "Learn to listen to understand, not to fix" },
              { num: 6, title: "The Weekly Reset", desc: "A structured conversation that prevents drift" },
              { num: 7, title: "The Physical Reconnection", desc: "Rebuild physical intimacy step by step" }
            ].map((habit) => (
              <div key={habit.num} className="flex items-start gap-4 bg-white/5 p-6 rounded-xl border border-white/10 hover:border-orange-accent/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-orange-accent flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{habit.num}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">{habit.title}</h3>
                  <p className="text-gray-400">{habit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-16 px-6 bg-navy-darker">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            What's Inside The Ebook
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Book, title: "6 Powerful Chapters", desc: "Comprehensive guide from understanding to action" },
              { icon: CheckCircle, title: "Step-by-Step System", desc: "Clear instructions for each habit" },
              { icon: Users, title: "Real Stories", desc: "Learn from husbands who transformed their marriages" },
              { icon: Award, title: "Research-Backed", desc: "Based on Gottman Institute findings" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-accent/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-green-accent" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            What Husbands Are Saying
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                quote: "After 15 years of marriage, I thought we'd lost our spark forever. These habits brought it back within weeks.",
                name: "Michael R.",
                detail: "Married 15 years"
              },
              {
                quote: "The Two-Sentence Text habit alone transformed how my wife sees me. She said she feels 'pursued' again.",
                name: "David K.",
                detail: "Married 8 years"
              },
              {
                quote: "I was skeptical, but the science behind these habits convinced me. The results convinced my wife.",
                name: "Thomas S.",
                detail: "Married 22 years"
              },
              {
                quote: "Finally, something practical. Not just 'communicate more' but actual steps I could take every day.",
                name: "Robert M.",
                detail: "Married 6 years"
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-gray-300 italic mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-gray-500 text-sm">{testimonial.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="py-16 px-6 bg-navy-darker">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">About The Author</h2>
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-accent to-green-accent mx-auto mb-6 flex items-center justify-center">
            <span className="text-white text-3xl font-bold">MD</span>
          </div>
          <h3 className="text-2xl font-semibold text-white mb-2">{PRODUCT.author}</h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Mark Dawdy is a relationship coach and author who has helped hundreds of couples 
            reconnect and rebuild their marriages. After transforming his own 20-year marriage 
            using these principles, he's dedicated his life to helping other husbands do the same.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                  data-testid={`faq-${i}`}
                >
                  <span className="text-white font-semibold">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-accent/20 to-green-accent/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready To Transform Your Marriage?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Don't wait until it's too late. Start implementing these habits today.
          </p>
          <div className="bg-navy-dark/80 backdrop-blur-lg rounded-2xl p-8 max-w-md mx-auto border border-white/20">
            <img
              src={PRODUCT.thumbnail}
              alt={PRODUCT.title}
              className="w-48 mx-auto mb-6 rounded-lg shadow-lg"
            />
            <h3 className="text-xl font-bold text-white mb-2">{PRODUCT.title}</h3>
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-gray-400 line-through text-xl">$47</span>
              <span className="text-3xl font-bold text-orange-accent">${PRODUCT.price}</span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="w-full py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105"
              data-testid="final-buy-btn"
            >
              GET INSTANT ACCESS NOW
            </button>
            <div className="mt-4 flex items-center justify-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> Secure Payment</span>
              <span className="flex items-center gap-1"><Download className="w-4 h-4" /> Instant Download</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2025 Mindful Forays. All rights reserved.</p>
          <p className="text-gray-600 text-xs mt-2">30-Day Money-Back Guarantee | Secure Checkout</p>
        </div>
      </footer>
    </div>
  );
};

// ========== CHECKOUT PAGE ==========
const CheckoutPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("funnel_email") || "");
  const [firstName, setFirstName] = useState(localStorage.getItem("funnel_name") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePurchase = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/purchases`, {
        email,
        first_name: firstName
      });
      localStorage.setItem("purchase_complete", "true");
      navigate("/thank-you");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark to-navy-darker py-12 px-6" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_SMALL} alt="Mindful Forays" className="h-10 mx-auto" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 h-fit">
            <h2 className="text-xl font-bold text-white mb-6">Order Summary</h2>
            <div className="flex gap-4 mb-6">
              <img
                src={PRODUCT.thumbnail}
                alt={PRODUCT.title}
                className="w-24 h-auto rounded-lg"
              />
              <div>
                <h3 className="text-white font-semibold">{PRODUCT.title}</h3>
                <p className="text-gray-400 text-sm">{PRODUCT.subtitle}</p>
                <p className="text-orange-accent font-bold mt-2">${PRODUCT.price}</p>
              </div>
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between text-gray-400 mb-2">
                <span>Subtotal</span>
                <span>$47.00</span>
              </div>
              <div className="flex justify-between text-green-accent mb-2">
                <span>Discount (43% off)</span>
                <span>-$20.00</span>
              </div>
              <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-white/10">
                <span>Total</span>
                <span>${PRODUCT.price}.00</span>
              </div>
            </div>

            {/* What you get */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="text-white font-semibold mb-4">What You Get:</h4>
              <ul className="space-y-2">
                {[
                  "Instant PDF Download",
                  "7 Marriage-Saving Habits System",
                  "Step-by-Step Implementation Guide",
                  "Real Success Stories",
                  "30-Day Money-Back Guarantee"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-accent flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6">Complete Your Order</h2>
            
            <form onSubmit={handlePurchase} className="space-y-4" data-testid="checkout-form">
              <div>
                <label className="block text-gray-300 text-sm mb-2">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-orange-accent transition-colors"
                  data-testid="checkout-firstname"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-orange-accent transition-colors"
                  data-testid="checkout-email"
                />
              </div>

              {/* Simulated card info (for demo) */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-gray-400 text-sm mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Secure checkout - your data is protected
                </p>
                <div className="bg-green-accent/10 border border-green-accent/30 rounded-lg p-4 mb-4">
                  <p className="text-green-accent text-sm text-center">
                    🎉 Demo Mode: Click below to complete your purchase and get instant access!
                  </p>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm" data-testid="checkout-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50"
                data-testid="checkout-submit"
              >
                {loading ? "Processing..." : `COMPLETE PURCHASE - $${PRODUCT.price}`}
              </button>

              <p className="text-gray-400 text-xs text-center">
                By purchasing, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>

            {/* Trust badges */}
            <div className="mt-6 pt-6 border-t border-white/10 flex justify-center gap-6">
              <div className="text-center">
                <Shield className="w-8 h-8 text-green-accent mx-auto mb-1" />
                <p className="text-gray-400 text-xs">Secure</p>
              </div>
              <div className="text-center">
                <Download className="w-8 h-8 text-green-accent mx-auto mb-1" />
                <p className="text-gray-400 text-xs">Instant</p>
              </div>
              <div className="text-center">
                <Award className="w-8 h-8 text-green-accent mx-auto mb-1" />
                <p className="text-gray-400 text-xs">Guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== THANK YOU PAGE ==========
const ThankYouPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not purchased
    if (!localStorage.getItem("purchase_complete")) {
      navigate("/");
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark to-navy-darker py-12 px-6" data-testid="thankyou-page">
      <div className="max-w-2xl mx-auto text-center">
        {/* Header */}
        <div className="mb-8">
          <img src={LOGO_SMALL} alt="Mindful Forays" className="h-10 mx-auto" />
        </div>

        {/* Success Animation */}
        <div className="w-24 h-24 rounded-full bg-green-accent/20 flex items-center justify-center mx-auto mb-8">
          <CheckCircle className="w-12 h-12 text-green-accent" />
        </div>

        <h1 className="text-4xl font-bold text-white mb-4">Thank You For Your Purchase!</h1>
        <p className="text-xl text-gray-300 mb-8">
          You're about to transform your marriage with the 7 Marriage-Saving Habits.
        </p>

        {/* Download Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-8">
          <img
            src={PRODUCT.thumbnail}
            alt={PRODUCT.title}
            className="w-48 mx-auto mb-6 rounded-lg shadow-lg"
          />
          <h2 className="text-2xl font-bold text-white mb-4">{PRODUCT.title}</h2>
          <a
            href={PRODUCT.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105"
            data-testid="download-btn"
          >
            <Download className="w-5 h-5" />
            DOWNLOAD YOUR EBOOK NOW
          </a>
          <p className="text-gray-400 text-sm mt-4">
            Click the button above to download your PDF instantly.
          </p>
        </div>

        {/* Next Steps */}
        <div className="bg-white/5 rounded-2xl p-8 border border-white/10 text-left">
          <h3 className="text-xl font-bold text-white mb-6 text-center">What's Next?</h3>
          <div className="space-y-4">
            {[
              { num: 1, title: "Download Your Ebook", desc: "Click the button above to get your PDF" },
              { num: 2, title: "Read Chapter 1 Today", desc: "Understand the foundation before implementing" },
              { num: 3, title: "Start With ONE Habit", desc: "Pick the easiest habit and do it tomorrow morning" },
              { num: 4, title: "Be Consistent", desc: "Commit to 30 days of small, daily actions" }
            ].map((step) => (
              <div key={step.num} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-accent flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">{step.num}</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">{step.title}</h4>
                  <p className="text-gray-400 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Support */}
        <div className="mt-8 text-gray-400">
          <p>Need help? Email us at <a href="mailto:info@mindfulforays.com" className="text-orange-accent hover:underline">info@mindfulforays.com</a></p>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-sm">© 2025 Mindful Forays. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

// ========== CHECKLIST DELIVERY PAGE ==========
const ChecklistPage = () => {
  const navigate = useNavigate();

  // Checklist items from the ebook
  const checklistItems = [
    {
      habit: "The Morning Check-In",
      action: "Start each day with a 5-minute intentional connection",
      checkbox: "Ask: 'What's one thing on your mind today?'"
    },
    {
      habit: "The Two-Sentence Text",
      action: "Send a thoughtful message during the day",
      checkbox: "Text her something appreciative or romantic"
    },
    {
      habit: "The Presence Power-Up",
      action: "Be fully present when together (phone away)",
      checkbox: "Give undivided attention for at least 15 minutes"
    },
    {
      habit: "The Appreciation Anchor",
      action: "Express specific gratitude daily",
      checkbox: "Share one specific thing you appreciate about her"
    },
    {
      habit: "The Listening Loop",
      action: "Listen to understand, not to fix",
      checkbox: "Ask follow-up questions instead of offering solutions"
    },
    {
      habit: "The Weekly Reset",
      action: "Have a structured weekly conversation",
      checkbox: "Schedule and hold your weekly reset meeting"
    },
    {
      habit: "The Physical Reconnection",
      action: "Rebuild physical intimacy step by step",
      checkbox: "Initiate non-sexual physical touch (hugs, holding hands)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark to-navy-darker py-12 px-6" data-testid="checklist-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_SMALL} alt="Mindful Forays" className="h-10 mx-auto" />
        </div>

        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-green-accent/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-accent" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your Free Checklist is Ready!
          </h1>
          <p className="text-gray-300">
            Print this or save it to your phone. Use it daily for the next 30 days.
          </p>
        </div>

        {/* Checklist Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-orange-accent mb-2">
              7 Marriage-Saving Habits Checklist
            </h2>
            <p className="text-gray-400">Daily Actions to Transform Your Marriage</p>
          </div>

          <div className="space-y-6">
            {checklistItems.map((item, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-accent flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">{item.habit}</h3>
                    <p className="text-gray-400 text-sm mb-3">{item.action}</p>
                    <div className="flex items-center gap-3 bg-navy-darker/50 rounded-lg p-3">
                      <div className="w-5 h-5 border-2 border-orange-accent rounded flex-shrink-0"></div>
                      <span className="text-gray-300 text-sm">{item.checkbox}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pro Tip */}
          <div className="mt-8 bg-green-accent/10 border border-green-accent/30 rounded-lg p-4">
            <p className="text-green-accent text-sm">
              <strong>Pro Tip:</strong> Start with just ONE habit. Master it for a week before adding another. 
              Consistency beats intensity every time.
            </p>
          </div>
        </div>

        {/* CTA to Sales Page */}
        <div className="bg-gradient-to-r from-orange-accent/20 to-green-accent/20 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-4">
            Want the Complete System?
          </h3>
          <p className="text-gray-300 mb-6">
            Get step-by-step implementation guides, real success stories, and the full 
            science behind each habit in the complete ebook.
          </p>
          <button
            onClick={() => navigate("/sales")}
            className="px-8 py-4 bg-orange-accent hover:bg-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105 inline-flex items-center gap-2"
            data-testid="checklist-cta"
          >
            GET THE COMPLETE EBOOK - $27
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-gray-400 text-xs mt-4">
            30-Day Money-Back Guarantee • Instant Download
          </p>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-gray-500 text-sm">© 2025 Mindful Forays. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

// ========== MAIN APP ==========
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SqueezePage />} />
          <Route path="/checklist" element={<ChecklistPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
